import { toast as toastify, type Id, type ToastContent, type ToastOptions } from 'react-toastify';

export interface ToastRoute {
  content: ToastContent;
  options?: ToastOptions;
}

/**
 * Return a toast id when the route is shown.
 * Return `false` to leave the toast on react-toastify.
 */
export interface ToastRouter {
  show: (route: ToastRoute) => Id | false;
  dismiss?: (id?: Id) => void;
}

let router: ToastRouter | undefined;

export function setToastRouter(next: ToastRouter | undefined) {
  router = next;
}

type ToastMethod = 'success' | 'info' | 'error' | 'warning';

type ToastCall = (content: ToastContent, options?: ToastOptions) => Id;

/** Methods this wrapper implements. A routed id cannot be passed to react-toastify `update` or `isActive`. */
export interface RoutedToast extends ToastCall {
  success: ToastCall;
  info: ToastCall;
  error: ToastCall;
  warning: ToastCall;
  warn: ToastCall;
  dismiss: (params?: Id | { id?: Id; containerId?: Id }) => void;
  loading: typeof toastify.loading;
  promise: typeof toastify.promise;
}

export const toast: RoutedToast = Object.assign(call, {
  loading: toastify.loading,
  promise: toastify.promise,
  success: bind('success'),
  info: bind('info'),
  error: bind('error'),
  warning: bind('warning'),
  warn: bind('warning'),
  dismiss,
});

function tryRoute(content: ToastContent, options?: ToastOptions): Id | false {
  if (!router) return false;
  return router.show({ content, options });
}

function call(content: ToastContent, options?: ToastOptions): Id {
  const routed = tryRoute(content, options);
  if (routed !== false) return routed;
  return toastify(content, options);
}

function bind(type: ToastMethod) {
  return (content: ToastContent, options?: ToastOptions): Id => {
    const routed = tryRoute(content, { ...options, type: options?.type ?? type });
    if (routed !== false) return routed;
    return toastify[type](content, options);
  };
}

function dismiss(params?: Id | { id?: Id; containerId?: Id }) {
  if (params != null && typeof params === 'object') {
    router?.dismiss?.(params.id);
    toastify.dismiss({ id: params.id, containerId: params.containerId ?? 1 });
    return;
  }

  router?.dismiss?.(params);
  toastify.dismiss(params);
}
